import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faChevronDown,
  faTag,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { TallyProductCatalog, type TallyProduct } from '../components/TallyProductCatalog'
import { whatsAppCtaProps } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/bra-piaoli'
const PAGE_CATEGORY = 'Bra Piaoli'
const SEARCH_TERM = 'piaoli'

function resolvePiaoliBraProducts(products: StorefrontProduct[]): TallyProduct[] {
  return products
    .filter((product) =>
      /\b(bra|bh)\b/i.test(product.name),
    )
    .map((product) => ({
      name: product.name,
      slug: product.slug,
      sku: product.sku,
      image: getProductPrimaryImage(product, { width: 760, format: 'webp', quality: 78 }),
    }))
}

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Pemesanan via WhatsApp',
    desc: 'Kirim nomor model bra Piaoli, jumlah lusin, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation.',
  },
  {
    icon: faBoxesStacked,
    title: 'Cek banyak model sekaligus',
    desc: 'Admin memeriksa jumlah, ukuran, dan warna yang tersedia setelah Anda mengirim daftar model atau foto referensi.',
  },
  {
    icon: faTag,
    title: 'Sample sebelum order besar',
    desc: 'Sample dihitung harga satuan ditambah ongkir. Gunakan untuk mengecek bahan, bentuk cup, jahitan, dan ukuran sebelum order grosir.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke toko, gudang, atau lokasi usaha',
    desc: 'Pesanan bisa diarahkan ke toko, gudang, atau alamat usaha lain di seluruh Indonesia.',
  },
]

const BRA_PIAOLI_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah semua model bra Piaoli selalu tersedia?',
    a: 'Stok berubah mengikuti produksi. Kirim nomor model seperti PL605, PL720, atau PL4800. Admin akan mengirim foto stok aktual, ukuran, dan warna yang tersedia.',
  },
  {
    q: 'Apakah Dropify sanggup melayani pesanan jauh di atas 25 lusin?',
    a: 'Sampaikan nomor model Piaoli dan total lusin yang dibutuhkan. Admin memeriksa jumlah yang tersedia dan menyiapkan quotation untuk volume tersebut.',
  },
  {
    q: 'Bagaimana cara memilih model bra Piaoli untuk toko?',
    a: 'Kirim jenis model yang dicari, misalnya wireless PL605 atau PL720, beserta kebutuhan ukuran. Admin akan mengirim foto, ukuran, dan warna model Piaoli yang tersedia.',
  },
  {
    q: 'Bagaimana kalau saya mau order ulang model yang sama bulan depan?',
    a: 'Simpan nomor model seperti PL605 atau PL720 dari pesanan sebelumnya. Kirim nomor tersebut bersama jumlah restock agar admin dapat memeriksa stok dan menyiapkan quotation baru.',
  },
  {
    q: 'Berapa lama pengiriman bra Piaoli dari Surabaya?',
    a: 'Setelah pembayaran dikonfirmasi, admin memberi informasi waktu penyiapan. Estimasi perjalanan dari Surabaya mengikuti kota tujuan dan layanan ekspedisi.',
  },
]

export const BRA_PIAOLI_FAQ: FaqItem[] = [
  {
    q: 'Apa saja model bra Piaoli yang tersedia di Dropify?',
    a: 'Tersedia berbagai model bra Piaoli seperti BH PL605 K3 Wireless Extra Comfort, BH PL006, BH PL720, BH PL005, BH PL003, BH PL1222, dan BH PL4800. Setiap model memiliki potongan dan rentang ukuran yang berbeda. Kirim nomor model saat bertanya agar admin mengecek stok yang tepat.',
  },
  {
    q: 'Berapa harga dan minimum order bra Piaoli?',
    a: 'MOQ bra Piaoli umumnya 25 lusin dengan isi 12 pcs per lusin. Harga mengikuti model dan jumlah. Admin akan mencantumkannya pada quotation setelah model pilihan Anda dipastikan.',
  },
  {
    q: 'Apakah ukuran dan warna bra Piaoli bisa dicampur?',
    a: 'Untuk kebanyakan model Piaoli, satu lusin boleh berisi campuran ukuran dan warna selama stoknya ada. Sebutkan komposisi yang Anda mau, admin akan mengecek ketersediaannya lebih dulu.',
  },
  {
    q: 'Bisakah memesan sampel bra Piaoli lebih dahulu?',
    a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat memeriksa bahan, bentuk cup, jahitan, elastisitas, label, dan ukuran secara langsung sebelum menentukan model untuk pesanan grosir.',
  },
  {
    q: 'Bagaimana cara memesan bra Piaoli di Dropify?',
    a: 'Sebutkan model Piaoli yang diminati, jumlah lusin, dan kota tujuan lewat WhatsApp. Admin membalas dengan stok aktual dan quotation, kemudian mengurus pembayaran sampai barang dikirim.',
  },
]

const THEME = {
  border: 'border-rose-100',
  bg: 'bg-rose-50',
  cardBg: 'bg-white',
  primaryText: 'text-rose-700',
  hoverText: 'text-rose-700',
  buttonBg: 'bg-rose-700',
  buttonText: 'text-white',
  ring: 'focus:ring-rose-100',
  placeholderBg: 'bg-[#fff8f8]',
}

export const Route = createFileRoute('/bra-piaoli')({
  loader: async () => {
    const [config, categories, response, proofImages] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ search: SEARCH_TERM, per_page: 100 }).catch(() => null),
      getBrandProofImages(),
    ])

    return {
      config,
      categories,
      products: resolvePiaoliBraProducts(response?.items ?? []),
      proofImages,
    }
  },
  head: ({ loaderData }) => {
    const title = brandTitle('Grosir Bra Piaoli untuk Toko & Reseller')
    const description =
      'Grosir bra Piaoli dengan MOQ umumnya 25 lusin. Bandingkan model PL605, PL720, PL4800, dan lainnya, lalu cek stok serta quotation melalui WhatsApp.'
    const products = loaderData?.products ?? []
    const heroImage = products[0]?.image

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        ...(heroImage ? [{ property: 'og:image', content: heroImage }] : []),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        ...(heroImage ? [{ name: 'twitter:image', content: heroImage }] : []),
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Piaoli', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Merek', path: '/merek' },
              { name: 'Bra Piaoli', path: PAGE_PATH },
            ]),
          ),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...BRA_PIAOLI_FAQ, ...BRA_PIAOLI_TRUST_QUESTIONS])) },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Bra Piaoli di Dropify',
            itemListElement: products.map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: product.name,
                sku: product.sku,
                image: product.image,
                url: absoluteUrl(`/produk/${product.slug}`),
                brand: { '@type': 'Brand', name: 'Piaoli' },
              },
            })),
          }),
        },
      ],
    }
  },
  component: BraPiaoliRoute,
})

function BraPiaoliRoute() {
  const { config, categories, products, proofImages } = Route.useLoaderData()
  return <BraPiaoliLandingPage config={config} categories={categories} products={products} proofImages={proofImages} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  className,
}: {
  message: string
  label: string
  placement: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-xs font-black uppercase tracking-[0.22em] text-rose-700">
      {children}
    </p>
  )
}

export function BraPiaoliLandingPage({
  config = null,
  categories = [],
  products = [],
  proofImages = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: TallyProduct[]
  proofImages?: string[]
}) {
  const heroProducts = products.slice(0, 4)
  const heroMessage =
    'Halo Dropify, saya mau cek bra Piaoli untuk stok toko. Nomor model yang diminati: ___. Kebutuhan: ___ lusin. Ukuran/warna: ___. Kota tujuan: ___. Bisa kirim stok aktual dan harganya?'

  return (
    <div className="min-h-screen bg-[#fff8f8] text-[#2b1118]">
      <div className="bg-rose-200 px-4 py-2 text-center text-xs font-black text-[#4b1324] sm:text-sm">
        {products.length} model bra Piaoli · MOQ mulai 25 lusin · 1 lusin isi 12 pcs
      </div>

      <header className="border-b border-white/10 bg-[#3d1020] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa stok bra Piaoli"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-rose-200/60 px-4 py-2.5 text-sm font-bold text-rose-100 transition-colors hover:bg-rose-200 hover:text-[#3d1020]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#3d1020] text-white">
          <div aria-hidden className="absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-rose-400/10 blur-3xl" />
          <div aria-hidden className="absolute -right-20 top-16 h-96 w-96 rounded-full border-[64px] border-rose-200/5" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="inline-flex rounded-full bg-rose-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#4b1324]">Grosir bra Piaoli</p>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                {products.length} model bra Piaoli untuk <span className="text-rose-200">melengkapi pilihan di toko Anda.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-rose-50/80 md:text-lg">
                Pilih model dari katalog, lalu kirim fotonya ke admin. Kami akan mengecek ukuran, warna, stok, dan harga untuk pesanan mulai 25 lusin.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Minta quotation bra Piaoli"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#model-bra-piaoli" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                  Lihat {products.length} model
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-5 text-sm text-rose-100/70">Harga dan stok dikonfirmasi per model melalui quotation.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {heroProducts.map((product, index) => (
                <article
                  key={product.sku}
                  data-hero-product-visual
                  className={`overflow-hidden rounded-3xl bg-white p-3 text-[#3d1020] shadow-2xl ${index % 2 === 1 ? 'translate-y-5' : '-translate-y-1'}`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    width="560"
                    height="560"
                    fetchPriority={index < 2 ? 'high' : undefined}
                    decoding="async"
                    className="aspect-square w-full rounded-2xl object-cover"
                  />
                  <div className="px-2 pb-2 pt-3">
                    <span className="text-sm font-black">{product.name}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BrandEasyOrder />

        <section id="model-bra-piaoli" className="scroll-mt-6 bg-[#fff8f8] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
              <div className="max-w-3xl">
                <SectionLabel>{`${products.length} model yang tersedia`}</SectionLabel>
                <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pilih model bra Piaoli yang ingin Anda cek</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">Klik foto untuk membuka detail produk. Saat menghubungi admin, kirim nomor model dan jumlah yang dibutuhkan untuk pemeriksaan stok.</p>
              </div>
            </div>

            <TallyProductCatalog
              products={products}
              pageCategory={PAGE_CATEGORY}
              listName="Bra Piaoli brand landing catalog"
              anchorId="katalog-bra-piaoli"
              theme={THEME}
              searchPlaceholder="Cari bra Piaoli berdasarkan nama atau nomor model"
              emptyMessage="Model tidak ditemukan."
            />
          </div>
        </section>

        <section className="bg-[#fff8f8] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionLabel>Pesan melalui Dropify</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kenapa toko memesan bra Piaoli di Dropify?</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Satu admin membantu mengecek nomor model, foto, ukuran, warna, jumlah, harga, dan pengiriman.</p>
              <WhatsAppButton message={heroMessage} label="Chat admin bra Piaoli" placement="why_dropify" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-rose-100 bg-white p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#3d1020] text-rose-200">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-rose-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <SectionLabel>FAQ Bra Piaoli</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Yang sering ditanyakan pembeli grosir</h2>
            </div>
            <div className="mt-9 divide-y divide-rose-100 border-y border-rose-100">
              {BRA_PIAOLI_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-[#2b1118]">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-rose-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-rose-200 py-14 text-[#3d1020]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Siapkan daftar model</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim foto atau nomor model, jumlah lusin, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Tambahkan kebutuhan ukuran dan warna agar pengecekan stok pertama lebih lengkap.</p>
            </div>
            <WhatsAppButton message={heroMessage} label="Minta quotation bra Piaoli" placement="final_cta" className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#3d1020] px-7 py-4 text-base font-black text-white transition-colors hover:bg-[#2b1118]" />
          </div>
        </section>

        <BrandTrustSections brandName="Piaoli" itemName="bra Piaoli" images={proofImages} questions={BRA_PIAOLI_TRUST_QUESTIONS} />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
